import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useStripe } from '@stripe/react-stripe-js'
import addDays from 'date-fns/addDays'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'

import { constants } from '@blocktickets/shared-utils'

import AuthService from '../../../../utilities/services/auth.service'
import { formatCurrency, getPaymentOption } from '../../../../utilities/helpers'
import {
  chargeDonationInvoice,
  getMyOrganizations,
  getPaymentIntent,
  recordDonationRequirement,
} from '../../../../utilities/api'
import CardForm from '../../../../providers/AssignToProvider/CardForm'
import { SelectPaymentDueDate } from '../../../../providers/AssignToProvider/SelectPaymentDueDate'
import { useModal } from '../../../../providers/ModalProvider/ModalProvider'
import { Error, Success } from '../../index'
import infoCardStyles from '../../../../providers/AssignToProvider/PricingV2/InfoCard/InfoCard.module.scss'

import './chargeDonationModal.scss'

const { PAYMENT_TYPE } = constants

const PAYMENT_METHODS = ['CARD', 'INVOICE']

function SummaryClockIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100
}

function salesTaxFromCombinedRate(taxableBase, storedCombinedRate) {
  const n = Number(storedCombinedRate)
  if (
    !Number.isFinite(n) ||
    n <= 0 ||
    !Number.isFinite(taxableBase) ||
    taxableBase <= 0
  ) {
    return 0
  }
  if (n <= 1) return round2(taxableBase * n)
  return round2(taxableBase * (n / 100))
}

function cardLikeProcessingFee(subtotal) {
  const base = Number(subtotal) || 0
  if (base <= 0) return 0
  return round2(0.3 + base * 0.029)
}

function computeDonationPriceSummary({
  donationBase,
  paymentMethodKey,
  organization,
}) {
  const base = Number(donationBase) || 0
  if (base <= 0) return null

  const pm = String(paymentMethodKey || 'CARD').toUpperCase()
  const processingFee =
    pm === 'CARD' || pm === 'INVOICE' ? cardLikeProcessingFee(base) : 0

  const storedRate =
    organization?.taxRate?.estimatedCombinedRate ??
    organization?.taxRate?.estimated_combined_rate ??
    organization?.tax_rate?.estimatedCombinedRate

  const taxableBase = base + processingFee
  let salesTax = 0
  if (organization?.isTaxable !== false) {
    salesTax = salesTaxFromCombinedRate(taxableBase, storedRate)
  }

  const totalCharge = round2(base + processingFee + salesTax)

  return {
    donationBaseAmount: base,
    serviceFee: 0,
    processingFee,
    salesTax,
    totalCharge,
  }
}

function ChargeDonationModalContent({
  show,
  onClose,
  requirement,
  fanProfile,
  organization,
  connectedAccountId,
  stripeAccountContext,
  onStripeAccountContextChange,
  onSuccess,
}) {
  const stripe = useStripe()
  const { openModal, closeModal: closeOverlayModal } = useModal()

  const [orgPricingSnapshot, setOrgPricingSnapshot] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState('')
  const [clientSecret, setClientSecret] = useState()
  const [paymentIntentId, setPaymentIntentId] = useState('')
  const [initialClientSecret, setInitialClientSecret] = useState()
  const [card, setCard] = useState()
  const [isCardValid, setIsCardValid] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [dueDateOpt, setDueDateOpt] = useState('30')
  const [customDueDate, setCustomDueDate] = useState(new Date())
  const [serverDonationPricing, setServerDonationPricing] = useState(null)
  const lastPaymentIntentKeyRef = useRef(null)
  const inFlightPaymentIntentKeyRef = useRef(null)

  const chargeAmount =
    Number(requirement?.minimumAmount) ||
    Number(requirement?.donation_rule?.minimumAmount) ||
    0

  const ruleName = requirement?.donation_rule?.name || 'Donation'

  const feeConfigOrg = useMemo(() => {
    if (
      orgPricingSnapshot?.uuid &&
      organization?.uuid &&
      String(orgPricingSnapshot.uuid) === String(organization.uuid)
    ) {
      return orgPricingSnapshot
    }
    return organization
  }, [organization, orgPricingSnapshot])

  useEffect(() => {
    if (!show) {
      setOrgPricingSnapshot(null)
      return undefined
    }
    const uuid = organization?.uuid
    if (!uuid) return undefined
    let cancelled = false
    getMyOrganizations()
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : []
        const match =
          list.find((o) => o?.uuid && String(o.uuid) === String(uuid)) ||
          list[0]
        if (match) setOrgPricingSnapshot(match)
      })
      .catch(() => {
        if (!cancelled) setOrgPricingSnapshot(null)
      })
    return () => {
      cancelled = true
    }
  }, [show, organization?.uuid])

  useEffect(() => {
    if (!show) {
      setPaymentMethod('')
      setClientSecret()
      setPaymentIntentId('')
      setInitialClientSecret()
      setIsCardValid(false)
      setDueDateOpt('30')
      setCustomDueDate(new Date())
      setServerDonationPricing(null)
      lastPaymentIntentKeyRef.current = null
      inFlightPaymentIntentKeyRef.current = null
    }
  }, [show])

  const clientDonationPricing = useMemo(() => {
    if (!feeConfigOrg || chargeAmount <= 0 || !paymentMethod) return null
    return computeDonationPriceSummary({
      donationBase: chargeAmount,
      paymentMethodKey: paymentMethod,
      organization: feeConfigOrg,
    })
  }, [chargeAmount, paymentMethod, feeConfigOrg])

  const donationPricing = useMemo(() => {
    if (serverDonationPricing) {
      return {
        donationBaseAmount:
          Number(serverDonationPricing.donationBaseAmount) || 0,
        serviceFee: Number(serverDonationPricing.donationFee) || 0,
        processingFee: Number(serverDonationPricing.processingFee) || 0,
        salesTax: Number(serverDonationPricing.salesTax) || 0,
        totalCharge: Number(serverDonationPricing.totalCharge) || 0,
      }
    }
    return clientDonationPricing
  }, [serverDonationPricing, clientDonationPricing])

  const assignTo = useMemo(
    () => ({
      firstName: fanProfile?.firstName || '',
      lastName: fanProfile?.lastName || '',
      email: fanProfile?.email || '',
      phoneNumber: fanProfile?.phoneNumber || null,
    }),
    [fanProfile]
  )

  const shouldRequestIntent =
    (paymentMethod === 'CARD' || paymentMethod === 'INVOICE') &&
    (donationPricing?.totalCharge || 0) > 0

  const paymentIntentKey = useMemo(() => {
    if (!shouldRequestIntent) return null
    return [
      paymentMethod,
      chargeAmount,
      donationPricing?.totalCharge ?? '',
      organization?.uuid,
      requirement?.id,
      assignTo?.email,
    ].join(':')
  }, [
    shouldRequestIntent,
    paymentMethod,
    chargeAmount,
    donationPricing?.totalCharge,
    organization?.uuid,
    requirement?.id,
    assignTo?.email,
  ])

  const loadPaymentIntent = useCallback(
    (requestKey) => {
      if (!requestKey || !requirement?.id) return
      if (
        inFlightPaymentIntentKeyRef.current === requestKey ||
        lastPaymentIntentKeyRef.current === requestKey
      ) {
        return
      }
      inFlightPaymentIntentKeyRef.current = requestKey
      setClientSecret()
      setPaymentIntentId('')
      setInitialClientSecret()
      setServerDonationPricing(null)

      getPaymentIntent({
        donation: {
          requirementId: requirement.id,
          donationBaseAmount: chargeAmount,
          organization,
          assignTo,
          paymentOption: getPaymentOption(paymentMethod),
          paymentMethod: PAYMENT_TYPE[paymentMethod],
        },
      })
        .then((res) => {
          if (res?.data?.pricing) {
            setServerDonationPricing(res.data.pricing)
          }
          const isDestinationCharge = Boolean(
            res?.data?.transfer_data?.destination
          )
          const desiredStripeAccountContext = isDestinationCharge
            ? null
            : connectedAccountId
          const didContextChange =
            desiredStripeAccountContext !== stripeAccountContext

          if (typeof onStripeAccountContextChange === 'function') {
            onStripeAccountContextChange(desiredStripeAccountContext)
          }

          if (didContextChange) {
            setClientSecret('')
            setTimeout(() => {
              setClientSecret(res?.data?.client_secret)
              setInitialClientSecret(res?.data?.client_secret)
              setPaymentIntentId(res?.data?.id || '')
            }, 50)
          } else {
            setClientSecret(res?.data?.client_secret)
            setInitialClientSecret(res?.data?.client_secret)
            setPaymentIntentId(res?.data?.id || '')
          }
          lastPaymentIntentKeyRef.current = requestKey
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (inFlightPaymentIntentKeyRef.current === requestKey) {
            inFlightPaymentIntentKeyRef.current = null
          }
        })
    },
    [
      requirement?.id,
      chargeAmount,
      organization,
      assignTo,
      paymentMethod,
      connectedAccountId,
      stripeAccountContext,
      onStripeAccountContextChange,
    ]
  )

  useEffect(() => {
    if (!shouldRequestIntent) {
      lastPaymentIntentKeyRef.current = null
      inFlightPaymentIntentKeyRef.current = null
      setClientSecret()
      return
    }
    if (!paymentIntentKey) return
    if (lastPaymentIntentKeyRef.current === paymentIntentKey) return
    loadPaymentIntent(paymentIntentKey)
  }, [shouldRequestIntent, paymentIntentKey, loadPaymentIntent])

  const resolveDueDate = () => {
    if (dueDateOpt === 'custom') return customDueDate
    const days = Number(dueDateOpt)
    if (Number.isFinite(days) && days >= 0) {
      return addDays(new Date(), days)
    }
    return new Date()
  }

  const canSubmit = useMemo(() => {
    if (!paymentMethod || !donationPricing) return false
    if (paymentMethod === 'INVOICE') {
      return Boolean(dueDateOpt) && Boolean(paymentIntentId)
    }
    return Boolean(clientSecret) && isCardValid
  }, [
    paymentMethod,
    donationPricing,
    dueDateOpt,
    clientSecret,
    isCardValid,
    paymentIntentId,
  ])

  const openSuccess = (description) => {
    openModal(
      Success,
      {
        title: 'Donation charged',
        description,
        onClose: () => {
          closeOverlayModal()
          onSuccess?.()
          onClose()
        },
      },
      { className: 'modal-md' }
    )
  }

  const openErr = (message) => {
    openModal(
      Error,
      {
        title: 'Payment failed',
        description: message,
        onClose: closeOverlayModal,
      },
      { className: 'modal-md' }
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit || isSaving) return

    setIsSaving(true)
    try {
      const totalCharge = donationPricing?.totalCharge || chargeAmount

      if (paymentMethod === 'CARD') {
        if (!stripe || !clientSecret) {
          throw new Error('Payment is still loading. Please wait.')
        }
        const payload = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card },
        })
        if (payload?.error) throw new Error(payload.error.message)

        const paymentIntentId = payload?.paymentIntent?.id || ''
        await recordDonationRequirement({
          requirementId: requirement.id,
          amount: totalCharge,
          paymentMethod: PAYMENT_TYPE.CARD,
          paymentIntentId,
          estimatedProcessingFee: donationPricing?.processingFee ?? 0,
          note: requirement.note || '',
        })

        openSuccess(
          `A donation of ${formatCurrency(totalCharge)} has been charged successfully.`
        )
        return
      }

      if (paymentMethod === 'INVOICE') {
        if (!paymentIntentId) {
          throw new Error('Payment is still loading. Please wait.')
        }

        await chargeDonationInvoice({
          requirementId: requirement.id,
          paymentIntentId,
          amount: totalCharge,
          estimatedProcessingFee: donationPricing?.processingFee ?? 0,
          note: requirement.note || '',
          dueDate: resolveDueDate()?.toISOString(),
          assignTo,
          organization,
        })

        openSuccess(
          `An invoice for ${formatCurrency(totalCharge)} has been sent to ${assignTo.email}.`
        )
      }
    } catch (err) {
      openErr(
        err?.message || 'The donation could not be completed. Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const ticketsAffected = requirement?.ticketsAffected || {}
  const ticketCount = ticketsAffected.count || 0

  return (
    <Modal.Body className="charge-donation-modal d-flex-column">
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">
          {requirement?.requirementYear} {ruleName}
        </h1>
      </div>

      <Form onSubmit={handleSubmit} className="d-flex-column flex-grow-1">
        <Stack gap={3}>
          <Form.Group controlId="donationPaymentType">
            <Form.Label>Payment Type</Form.Label>
            <Form.Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="">Select payment type</option>
              {PAYMENT_METHODS.map((key) => (
                <option key={key} value={key}>
                  {key === 'CARD' ? 'Card' : 'Invoice'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {paymentMethod === 'CARD' && Boolean(clientSecret) && (
            <Card className="card card-with-border charge-donation-modal__card-panel">
              <Card.Body>
                <h2 className="charge-donation-modal__panel-title">
                  Add card information
                </h2>
                <CardForm
                  client_secret={clientSecret}
                  initialClientSecret={initialClientSecret}
                  setIsCardValid={setIsCardValid}
                  setCard={setCard}
                  setStripe={() => {}}
                  selectedCard={undefined}
                  setSelectedCard={() => {}}
                />
              </Card.Body>
            </Card>
          )}

          {paymentMethod === 'CARD' && shouldRequestIntent && !clientSecret && (
            <p className="text-muted small mb-0">Loading payment form…</p>
          )}

          {paymentMethod === 'INVOICE' && (
            <SelectPaymentDueDate
              dueDateOpt={dueDateOpt}
              setDueDateOpt={setDueDateOpt}
              customDueDate={customDueDate}
              setCustomDueDate={setCustomDueDate}
            />
          )}

          {donationPricing && paymentMethod && (
            <Card className={`${infoCardStyles.wrap} card-with-border`}>
              <Card.Body className={infoCardStyles.cardBody}>
                <div className={infoCardStyles.header}>
                  <SummaryClockIcon className={infoCardStyles.headerIcon} />
                  <div className={infoCardStyles.headerText}>
                    {paymentMethod === 'INVOICE' ? (
                      <>
                        A Stripe payment link will be sent to{' '}
                        <span className="text-primary">{assignTo.email}</span>
                      </>
                    ) : (
                      <>
                        Amount to charge on card:{' '}
                        <strong>
                          {formatCurrency(donationPricing.totalCharge)}
                        </strong>
                      </>
                    )}
                  </div>
                </div>
                <div className={infoCardStyles.summary}>
                  <div className={infoCardStyles.summaryTitle}>
                    Order summary
                  </div>
                  <div className={infoCardStyles.rowGroup}>
                    <div className={infoCardStyles.row}>
                      <span>
                        {ticketCount > 0
                          ? `Donation (${formatCurrency(donationPricing.donationBaseAmount)} · ${ticketCount} seats)`
                          : `Donation`}
                      </span>
                      <span>
                        {formatCurrency(donationPricing.donationBaseAmount)}
                      </span>
                    </div>
                    {donationPricing.serviceFee > 0 && (
                      <div className={infoCardStyles.row}>
                        <span>Service Fee</span>
                        <span>
                          {formatCurrency(donationPricing.serviceFee)}
                        </span>
                      </div>
                    )}
                  </div>
                  <hr className={infoCardStyles.dividerDashed} />
                  <div className={infoCardStyles.rowGroup}>
                    <div className={infoCardStyles.row}>
                      <span>Sales Tax</span>
                      <span>{formatCurrency(donationPricing.salesTax)}</span>
                    </div>
                    <div className={infoCardStyles.row}>
                      <span>Processing Fee</span>
                      <span>
                        {formatCurrency(donationPricing.processingFee)}
                      </span>
                    </div>
                  </div>
                  <hr className={infoCardStyles.dividerSolid} />
                  <div className={infoCardStyles.footerRow}>
                    <span>Total</span>
                    <span>{formatCurrency(donationPricing.totalCharge)}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </Stack>

        <Stack
          direction="horizontal"
          gap={2}
          className="mt-4 justify-content-end"
        >
          <Button type="button" variant="outline-light" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit || isSaving}
          >
            {isSaving
              ? 'Processing…'
              : paymentMethod === 'INVOICE'
                ? 'Send invoice'
                : 'Charge donation'}
          </Button>
        </Stack>
      </Form>
    </Modal.Body>
  )
}

export default function ChargeDonationModal({
  show,
  onClose,
  requirement,
  fanProfile,
  organization,
  onSuccess,
}) {
  const organizations = useMemo(() => AuthService.getOrg() || [], [])
  const paymentOrg = organization || organizations?.[0]

  const isProd = process.env.REACT_APP_STAGE === 'production'
  const connectedAccountId = isProd
    ? paymentOrg?.connected_account_id
    : paymentOrg?.test_connected_account_id

  const isDemoOrPreview =
    process.env.REACT_APP_STAGE === 'demo' ||
    process.env.REACT_APP_STAGE === 'preview'

  const [stripeAccountContext, setStripeAccountContext] = useState(() =>
    paymentOrg?.destinationAccount === false
      ? connectedAccountId
      : isDemoOrPreview || paymentOrg?.destinationAccount === true
        ? null
        : connectedAccountId
  )

  const stripePromise = useMemo(
    () =>
      loadStripe(
        process.env.REACT_APP_STRIPE_KEY,
        stripeAccountContext
          ? { stripeAccount: stripeAccountContext }
          : undefined
      ),
    [stripeAccountContext]
  )

  useEffect(() => {
    if (show) return
    setStripeAccountContext(
      paymentOrg?.destinationAccount === false
        ? connectedAccountId
        : isDemoOrPreview || paymentOrg?.destinationAccount === true
          ? null
          : connectedAccountId
    )
  }, [
    show,
    connectedAccountId,
    isDemoOrPreview,
    paymentOrg?.destinationAccount,
  ])

  if (!requirement) return null

  return (
    <Modal
      centered
      animation={false}
      fullscreen="md-down"
      show={show}
      onHide={onClose}
      backdrop="static"
      className="modal-lg charge-donation-modal__dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>Charge donation</Modal.Title>
      </Modal.Header>
      <Elements stripe={stripePromise}>
        <ChargeDonationModalContent
          show={show}
          onClose={onClose}
          requirement={requirement}
          fanProfile={fanProfile}
          organization={paymentOrg}
          connectedAccountId={connectedAccountId}
          stripeAccountContext={stripeAccountContext}
          onStripeAccountContextChange={setStripeAccountContext}
          onSuccess={onSuccess}
        />
      </Elements>
    </Modal>
  )
}
